import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DataTable,
  FilterBar,
  Notice,
  SortState,
} from '../patterns';
import type { FilterBarChip } from '../patterns';
import { getInventoryMovements } from '../../services/nutritionService';
import type { FertilizerMovement } from '../../services/nutritionService';
import { formatMeasure } from '../../utils/formatMeasure';
import { formatDate } from '../../dateFormat';

type TxTypeFilter = 'all' | 'purchase' | 'consumption' | 'adjustment';

const formatQty = (mov: FertilizerMovement): string => {
  // We pass Math.abs(mov.quantity) into formatMeasure, so the sign that used
  // to fall out "for free" from a negative mov.quantity (e.g. consumption rows)
  // must be made explicit here — otherwise abs() would silently flip a negative
  // ledger quantity to a positive-looking one.
  const sign = mov.quantity > 0
    ? (mov.type === 'purchase' || mov.type === 'adjustment' ? '+' : '')
    : (mov.quantity < 0 ? '-' : '');
  const unit = mov.unit ?? '';
  return `${sign}${formatMeasure(Math.abs(mov.quantity), unit)}`;
};

const NutritionInventoryMovements: React.FC = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<FertilizerMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState<TxTypeFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ key: 'date', direction: 'desc' });

  useEffect(() => {
    setLoading(true);
    setError('');
    getInventoryMovements()
      .then((data) => {
        setRows(data);
        setLoading(false);
      })
      .catch((err: { message?: string }) => {
        setError(err.message ?? t('common.error', { defaultValue: 'Failed to load' }));
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chips: FilterBarChip[] = [
    { id: 'all',         label: t('nutrition.inventory.movements.type.all',         { defaultValue: 'All' }),         active: typeFilter === 'all' },
    { id: 'purchase',    label: t('nutrition.inventory.movements.type.purchase',    { defaultValue: 'Purchase' }),    active: typeFilter === 'purchase' },
    { id: 'consumption', label: t('nutrition.inventory.movements.type.consumption', { defaultValue: 'Consumption' }), active: typeFilter === 'consumption' },
    { id: 'adjustment',  label: t('nutrition.inventory.movements.type.adjustment',  { defaultValue: 'Adjustment' }),  active: typeFilter === 'adjustment' },
  ];

  const handleChipClick = (id: string) => {
    if (id === 'all' || id === 'purchase' || id === 'consumption' || id === 'adjustment') {
      setTypeFilter(id as TxTypeFilter);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((mov) => {
      if (typeFilter !== 'all' && mov.type !== typeFilter) return false;
      if (!q) return true;
      return (
        mov.productName.toLowerCase().includes(q) ||
        mov.type.toLowerCase().includes(q) ||
        (mov.referenceId ?? '').toLowerCase().includes(q) ||
        formatDate(mov.date).toLowerCase().includes(q)
      );
    });
  }, [rows, search, typeFilter]);

  const sortedRows = useMemo(() => {
    const dir = sort.direction === 'asc' ? 1 : -1;
    const cmpStr = (a: string, b: string) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    return [...filtered].sort((a, b) => {
      switch (sort.key) {
        case 'type':      return cmpStr(a.type, b.type) * dir;
        case 'product':   return cmpStr(a.productName, b.productName) * dir;
        case 'quantity':  return (a.quantity - b.quantity) * dir;
        case 'reference': return cmpStr(a.referenceId ?? '', b.referenceId ?? '') * dir;
        case 'date':
        default: {
          return (new Date(a.date).getTime() - new Date(b.date).getTime()) * dir;
        }
      }
    });
  }, [filtered, sort]);

  const qtyClass = (mov: FertilizerMovement): string => {
    if (mov.type === 'purchase' && mov.quantity > 0) return 'ntr-qty--positive';
    if (mov.quantity < 0) return 'ntr-qty--negative';
    return '';
  };

  if (loading) return <div>{t('common.loading')}</div>;
  if (error) return <Notice tone="danger" role="alert">{error}</Notice>;

  return (
    <div data-testid="nutrition-inventory-movements">
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        chips={chips}
        onChipClick={handleChipClick}
      />
      <DataTable<FertilizerMovement>
        rows={sortedRows}
        rowKey={(mov) => mov._id}
        sort={sort}
        onSortChange={setSort}
        columns={[
          {
            key: 'date',
            label: t('nutrition.inventory.movements.col.date', { defaultValue: 'Date' }),
            sortable: true,
            render: (mov) => formatDate(mov.date),
          },
          {
            key: 'type',
            label: t('nutrition.inventory.movements.col.type', { defaultValue: 'Type' }),
            sortable: true,
            render: (mov) =>
              t(`nutrition.inventory.movements.type.${mov.type}`, { defaultValue: mov.type }),
          },
          {
            key: 'product',
            label: t('nutrition.inventory.movements.col.product', { defaultValue: 'Product' }),
            sortable: true,
            render: (mov) => mov.productName,
          },
          {
            key: 'quantity',
            label: t('nutrition.inventory.movements.col.quantity', { defaultValue: 'Quantity' }),
            align: 'right',
            sortable: true,
            render: (mov) => (
              <span className={qtyClass(mov)}>{formatQty(mov)}</span>
            ),
          },
          {
            key: 'reference',
            label: t('nutrition.inventory.movements.col.reference', { defaultValue: 'Reference' }),
            sortable: true,
            render: (mov) => mov.referenceId ?? '',
          },
        ]}
        emptyState={t('nutrition.inventory.movements.empty', { defaultValue: 'No movements recorded yet.' })}
        mobileCardRow={(mov) => (
          <div>
            <strong>
              {formatDate(mov.date)} ·{' '}
              {t(`nutrition.inventory.movements.type.${mov.type}`, { defaultValue: mov.type })}
            </strong>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              {mov.productName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>
              <span className={qtyClass(mov)}>{formatQty(mov)}</span>
              {mov.referenceId ? ` · ${mov.referenceId}` : ''}
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default NutritionInventoryMovements;
